import { z } from 'zod';

/**
 * The automations an agent is meant to run, kept in one memory block on its
 * config thread. Written when the agent is created from a role, so the agent
 * keeps its job description even before any of that work is built.
 *
 * Each entry is an automations-catalog activity. `automationId` links it to
 * the workspace automation built from that template, once one exists; the rail
 * resolves draft vs published from that link at read time, so the block never
 * stores a status that could go stale.
 */
export const PLANNED_AUTOMATIONS_BLOCK_NAME = 'Planned Automations';

const plannedSchema = z.object({
  /** Automations-catalog activity id. */
  activityId: z.string().min(1),
  /** The activity's name, for the agent to read and the rail to show. */
  name: z.string().min(1),
  /** The workspace automation built from this activity, once there is one. */
  automationId: z.string().min(1).optional(),
});
export type TPlannedAutomation = z.infer<typeof plannedSchema>;

const registrySchema = z.object({
  version: z.literal(1),
  automations: z.array(plannedSchema),
});

const HEADER =
  'Automations this agent is meant to run. Only the ones published in the workspace can be invoked; if a request needs one that is not available yet, say it is not built yet rather than doing the work another way.\n';

/**
 * Read the block's content. Strict, so an unreadable block is reported rather
 * than silently treated as an empty plan (and then overwritten by one).
 *
 * @param content - The memory block's content
 * @returns The planned automations, in order
 */
export function parsePlannedAutomations(
  content?: string,
): TPlannedAutomation[] {
  if (!content) return [];
  return registrySchema.parse(
    JSON.parse(
      content.startsWith(HEADER) ? content.slice(HEADER.length) : content,
    ),
  ).automations;
}

/**
 * Write the block's content: a line telling the agent what the list means,
 * then the list as JSON.
 *
 * @param automations - The planned automations
 * @returns The memory block content
 */
export function serializePlannedAutomations(
  automations: TPlannedAutomation[],
): string {
  return (
    HEADER +
    JSON.stringify(registrySchema.parse({ version: 1, automations }), null, 2)
  );
}

/**
 * Record that a planned automation now has a workspace automation behind it.
 *
 * @param automations - The current plan
 * @param activityId - The planned activity
 * @param automationId - The workspace automation built from it
 * @returns The plan with that entry linked; unchanged if the activity isn't in it
 */
export function linkPlannedAutomation(
  automations: TPlannedAutomation[],
  activityId: string,
  automationId: string,
): TPlannedAutomation[] {
  return automations.map(entry =>
    entry.activityId === activityId ? { ...entry, automationId } : entry,
  );
}

/**
 * The slug the `automations` tool source names a published automation's tools
 * with, derived from its display name. Used to find a planned automation's
 * tools once it's published, since tools don't carry the automation's id.
 *
 * @param displayName - The automation's display name
 * @returns Lowercase words joined by underscores
 */
export function automationToolSlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Why a planned automation isn't doing its part of the agent's job yet:
 * not built, a draft, deactivated, published but not turned on for the agent,
 * or published with no tools the agent can be given.
 */
export type TPlannedAutomationGapReason =
  | 'notBuilt'
  | 'draft'
  | 'inactive'
  | 'off'
  | 'noTools';

/** A planned automation that isn't doing its part of the agent's job yet. */
export interface IPlannedAutomationGap {
  activityId: string;
  name: string;
  reason: TPlannedAutomationGapReason;
}
